import React, {useEffect, useState} from 'react';
import {Input, message, Modal, Tooltip} from "antd";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {
   DataTable,
   PageCard,
   PageShell,
   PageToolbar,
   RowIconButton,
   StatusBadge,
} from "../../components/ui";

const statusTones = {
   process: 'blue',
   accept: 'green',
   cancel: 'red',
};

function StoreRequest(props) {
   const { t } = useTranslation();
   const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
   const [requests, setRequests] = useState([])
   const [selectedRequest, setSelectedRequest] = useState(null);
   const [isModalVisible, setIsModalVisible] = useState(false);
   const [actionType, setActionType] = useState(null); // To distinguish between accept and cancel
   const [description, setDescription] = useState(''); // For storing the description input
   const [isDescriptionModalVisibleForAddComment, setIsDescriptionModalVisibleForAddComment] = useState(false);
   const [isDescriptionModalVisible, setIsDescriptionModalVisible] = useState(false);
   const [fullDescription, setFullDescription] = useState('');
   const [loading, setLoading] = useState(false);

   const openDescriptionModal = (desc) => {
      setFullDescription(desc);
      setIsDescriptionModalVisible(true);
   };
   const openModal = (request) => {
      setSelectedRequest(request);
      setIsModalVisible(true);
   };

   const closeModal = () => {
      setIsModalVisible(false);
      setSelectedRequest(null);
   };

   const getRequests = async (page = 1, pageSize = 20) => {
      setLoading(true);
      try {
         const response = await axiosInstance.get(`/financiers/application/paginate`, {
            params: {
               page,
               per_page: pageSize,
            },
         });
         const { data, meta } = response.data;
         setRequests(data);
         setPagination({
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
         });
      } catch (error) {
         console.error("Error fetching requests:", error);
         message.error(t('store.fetchError'));
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      getRequests();
   }, []);

   const handleTableChange = (pagination) => {
      getRequests(pagination.current, pagination.pageSize);
   };

   const renderStatusTag = (status) => (
       <StatusBadge tone={statusTones[status] ?? 'muted'}>{t(`status.${status}`, { defaultValue: t('common.unknown') })}</StatusBadge>
   );

   const linkButton = (label, onClick) => (
       <button
           type="button"
           onClick={onClick}
           className="text-left text-sm font-medium text-[#1D2C7B] hover:underline"
       >
          {label}
       </button>
   );

   // Handle action button clicks (accept, cancel)
   const handleActionClick = (applicationId, warehouseId, type) => {
      setSelectedRequest({applicationId, warehouseId});
      setActionType(type);
      setIsDescriptionModalVisibleForAddComment(true); // Open the description modal
   };

   // Handle the final action with description validation
   const handleFinalAction = async () => {
      if (description.length < 10) {
         message.error(t('store.commentMin'));
         return;
      }

      const payload = {
         warehouse_id: selectedRequest.warehouseId,
         application_id: selectedRequest.applicationId,
         status: actionType,
         comment: description
      };

      try {
         const response = await axiosInstance.post('/financiers/check/application', payload);
         if (response.data.status) {
            message.success(response.data.message);
            setIsDescriptionModalVisibleForAddComment(false);
            setDescription(''); // Clear the description input
         } else {
            message.error(response.data.message);
         }
      } catch (error) {
         console.error(`Error setting request to ${actionType}:`, error);
         message.error(`Error setting request to ${actionType}`);
      }

      getRequests(); // Refresh the table after action
   };

   const rowActions = (record) => (
       record.status !== 'accept' && record.status !== 'cancel' ? (
           <div className="flex items-center gap-0.5">
              <Tooltip title={t('store.accept')}>
                 <RowIconButton
                     className="hover:!text-emerald-600"
                     onClick={() => handleActionClick(record.id, record.warehouse.id, 'accept')}
                 >
                    <CheckCircleIcon className="w-[18px] h-[18px]"/>
                 </RowIconButton>
              </Tooltip>
              <Tooltip title={t('store.reject')}>
                 <RowIconButton
                     className="hover:!text-red-600"
                     onClick={() => handleActionClick(record.id, record.warehouse.id, 'cancel')}
                 >
                    <XCircleIcon className="w-[18px] h-[18px]"/>
                 </RowIconButton>
              </Tooltip>
           </div>
       ) : null
   );

   const columns = [
      {
         title: t('common.id'),
         dataIndex: 'id',
         key: 'id',
         width: 72,
         render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
      },
      {
         title: t('common.qty'),
         dataIndex: 'qty',
         key: 'qty',
         width: 90,
         render: (qty) => <span className="text-sm text-slate-700 tabular-nums">{qty}</span>,
      },
      {
         title: t('common.date'),
         width: 170,
         dataIndex: 'created_at',
         key: 'created_at',
         render: (value) => <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{value}</span>,
      },
      {
         title: t('store.sender'),
         dataIndex: 'sender',
         key: 'sender',
         render: (sender) => linkButton(sender.name, () => openModal(sender))
      },
      {
         title: t('store.warehouse'),
         dataIndex: 'warehouse',
         key: 'warehouse',
         render: (warehouse) => linkButton(warehouse.name, () => openModal(warehouse))
      },
      {
         title: t('store.confirmer'),
         dataIndex: 'receive',
         width: 180,
         key: 'receive',
         render: (receive) => (
             receive
                 ? linkButton(receive.name, () => openModal(receive))
                 : <StatusBadge tone="muted">{t('store.notConfirmed')}</StatusBadge>
         )
      },
      {
         title: t('common.status'),
         dataIndex: 'status',
         width: 170,
         key: 'status',
         render: (status) => renderStatusTag(status)
      },
      {
         title: t('store.remarks'), // New column for comments
         dataIndex: 'warehouse',
         width: 130,
         key: 'comments',
         render: (warehouse, record) => {
            // Assuming that the comment is within `warehouse.histories`
            const history = warehouse.histories.find((hist) => hist.id === record.id);
            const comment = history?.history?.comment;

            return comment ? (
                comment.length > 10 ? (
                    linkButton(`${comment.substring(0, 10)}...`, () => openDescriptionModal(comment))
                ) : (
                    <span className="text-sm text-slate-700">{comment}</span>
                )
            ) : (
                <span className="text-xs text-slate-500">{t('store.noRemark')}</span>
            );
         },
      },
      {
         title: t('common.actions'),
         key: 'actions',
         width: 96,
         render: (_, record) => rowActions(record)
      }
   ];

   const renderMobileCard = (record) => (
       <div>
          <div className="flex items-start gap-3">
             <div className="min-w-0 flex-1">
                {linkButton(record.warehouse?.name, () => openModal(record.warehouse))}
                <span className="block text-xs text-slate-500 mt-0.5">{record.sender?.name}</span>
             </div>
             {rowActions(record)}
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
             {renderStatusTag(record.status)}
             <span className="tabular-nums">{record.qty}</span>
             <span className="tabular-nums">{record.created_at}</span>
          </div>
       </div>
   );

    return (
        <PageShell>
           <PageCard>
              <PageToolbar title={t('store.requests')} count={pagination.total}/>

              <DataTable
                  loading={loading}
                  columns={columns}
                  dataSource={requests}
                  rowKey="id"
                  scrollX={1200}
                  mobileCard={renderMobileCard}
                  pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      position: ['bottomCenter'],
                      className: 'px-4 pb-3',
                  }}
                  onChange={handleTableChange}/>
           </PageCard>

            {/* Modal for showing details */}
            <Modal
                title={t('store.details')}
                open={isModalVisible}
                onCancel={closeModal}
                footer={null}
                width="100%"
                style={{maxWidth: 520}}
            >
                {selectedRequest && (
                    <div className="text-sm text-slate-700">
                        <p><strong>{t('store.name')}:</strong> {selectedRequest?.name}</p>
                        {
                            selectedRequest?.phone && (
                                <p>
                                   <strong>{t('store.phoneNumber')}:</strong>{' '}
                                   <a href={`tel:+${selectedRequest?.phone}`} className="text-[#1D2C7B] tabular-nums">
                                      {selectedRequest?.phone}
                                   </a>
                                </p>
                            )
                        }
                        {selectedRequest?.category && (
                            <>
                                <p><strong>{t('common.category')}:</strong> {selectedRequest?.category.name}</p>
                                {/*<p><strong>Миқдор:</strong> {selectedRequest?.qty}</p>*/}
                                {/*<p><strong>Нархи:</strong> {selectedRequest?.price}</p>*/}
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* Modal for entering description */}
            <Modal
                title={actionType === 'accept' ? t('store.commentForAccept') : t('store.commentForReject')}
                open={isDescriptionModalVisibleForAddComment}
                onCancel={() => setIsDescriptionModalVisibleForAddComment(false)}
                onOk={handleFinalAction}
              okText={actionType === 'accept' ? t('store.accept') : t('store.reject')}
              width="100%"
              style={{maxWidth: 520}}
          >
             <Input.TextArea
                 placeholder={t('store.commentPh')}
                 value={description}
                 onChange={(e) => setDescription(e.target.value)}
                 rows={4}
             />
          </Modal>

          <Modal
              title={t('store.fullRemark')}
              open={isDescriptionModalVisible}
              onCancel={() => setIsDescriptionModalVisible(false)}
              footer={null}
              width="100%"
              style={{maxWidth: 520}}
          >
             <p className="text-sm text-slate-700">{fullDescription}</p>
          </Modal>
       </PageShell>
   );
}

export default StoreRequest;
